import {
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useRef,
} from 'react';

/** How much of the report the tray may take, as a share of the column. */
export const DRILL_TRAY_MIN = 25;
export const DRILL_TRAY_MAX = 75;
export const DRILL_TRAY_DEFAULT = 60;

export interface DrillTrayChip {
  key: string;
  label: string;
  value: string;
  /** Pinned by the most recent click, not carried from further up the chain. */
  fresh?: boolean;
}

export interface DrillTrayProps {
  /** The chain, source first: `Sr. Controller Trend › Clientwise › Timesheet entries`. */
  lineage: readonly string[];
  title: string;
  /** One line under the title: what this landing is and what it is not. */
  note?: string;
  /** The number clicked, kept in view so the landing can be checked against it. */
  figure: { label: string; text: string };
  /** The filters this landing carries in, coordinate and source alike. */
  chips: readonly DrillTrayChip[];
  /** One level back up the chain. Absent at the first level. */
  back?: { label: string; onBack: () => void };
  /** Closes the WHOLE chain. */
  onClose: () => void;
  /** The host's buttons — Export, Open as its own report. */
  actions?: ReactNode;
  /** Share of the report's column, in percent. */
  height: number;
  onResize: (percent: number) => void;
  /** The landing itself. */
  children: ReactNode;
}

/**
 * The drill, docked over the bottom of the report.
 *
 * NOT A SIDEBAR AND NOT A MODAL. A landing is often a listing twenty columns
 * wide, paged and exportable; a sidebar would be a second, narrower table, and
 * a modal would hide the report the figure came from. A split keeps both: the
 * report above, still scrollable, and what is under the number below it.
 *
 * It must read as NOT another report. So it inverts the page, the way the
 * dashboard's drill panel does: a recessed tray — muted surface, inset shadow
 * — holding a card, with an eyebrow naming where it came from.
 *
 * Sized like the SQL disclosure it sits beside: it does not flex-shrink, and it
 * takes a share of the column rather than a height of its own — 60% unless the
 * reader drags the edge. A second drill REPLACES the landing in place; the
 * lineage above the title is how the reader knows where they are, Back takes
 * one level off, ✕ closes them all.
 */
export function DrillTray({
  lineage,
  title,
  note,
  figure,
  chips,
  back,
  onClose,
  actions,
  height,
  onResize,
  children,
}: DrillTrayProps) {
  const ref = useRef<HTMLElement>(null);
  const dragging = useRef(false);

  const clamp = (n: number) => Math.round(Math.max(DRILL_TRAY_MIN, Math.min(DRILL_TRAY_MAX, n)));
  const fromPointer = (clientY: number) => {
    const column = ref.current?.parentElement?.getBoundingClientRect();
    if (!column || column.height <= 0) return;
    onResize(clamp(((column.bottom - clientY) / column.height) * 100));
  };
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    dragging.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (dragging.current) fromPointer(event.clientY);
  };
  const onPointerUp = () => {
    dragging.current = false;
  };
  const onHandleKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'ArrowUp' ? 5 : event.key === 'ArrowDown' ? -5 : 0;
    if (!step) return;
    onResize(clamp(height + step));
    event.preventDefault();
  };

  return (
    // Escape closes the chain — from INSIDE the tray only. A page-wide
    // listener would also fire behind any dialog opened over the report.
    <section
      ref={ref}
      className="rk-drilltray"
      aria-label={`Drill-down: ${title}`}
      style={{ '--rk-drill-height': `${height}%` } as CSSProperties}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !event.defaultPrevented) onClose();
      }}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: a focusable separator is the ARIA pattern for a resizable split */}
      <div
        className="rk-drilltray-handle"
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize the drill-down"
        aria-valuemin={DRILL_TRAY_MIN}
        aria-valuemax={DRILL_TRAY_MAX}
        aria-valuenow={height}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onHandleKey}
      />
      <nav className="rk-drilltray-lineage" aria-label="Drill path">
        <span aria-hidden className="rk-drilltray-lineage-icon">
          ↳
        </span>
        {lineage.map((step, i) => (
          <span
            // The path so far: unique per position, stable across renders.
            key={lineage.slice(0, i + 1).join('›')}
            className={i === lineage.length - 1 ? 'rk-drilltray-here' : undefined}
            aria-current={i === lineage.length - 1 ? 'page' : undefined}
          >
            {i > 0 && <span className="rk-drilltray-sep">›</span>}
            {step}
          </span>
        ))}
      </nav>

      <header className="rk-drilltray-head">
        <div className="rk-drilltray-titles">
          <h3 className="rk-drilltray-title">{title}</h3>
          {note && <p className="rk-drilltray-note">{note}</p>}
        </div>
        <div className="rk-drilltray-figure">
          <span className="rk-drilltray-figurelabel">{figure.label}</span>
          <span className="rk-drilltray-figuretext">{figure.text}</span>
        </div>
        <div className="rk-drilltray-actions">
          {back && (
            <button type="button" className="rk-drilltray-btn" onClick={back.onBack}>
              {back.label}
            </button>
          )}
          {actions}
          <button
            type="button"
            className="rk-drilltray-btn rk-drilltray-close"
            aria-label="Close the drill-down"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
      </header>

      {chips.length > 0 && (
        <ul className="rk-drilltray-chips" aria-label="Filters carried in">
          <li className="rk-drilltray-chipslabel" aria-hidden>
            Filters carried in
          </li>
          {chips.map((chip) => (
            <li key={chip.key} className={`rk-drilltray-chip${chip.fresh ? ' rk-fresh' : ''}`}>
              <span>{chip.label}:</span> {chip.value}
            </li>
          ))}
        </ul>
      )}

      <div className="rk-drilltray-body">{children}</div>
    </section>
  );
}
